/**
 * Jul-2026 (User-Wunsch) — „Barcode nacherfassen"-QR auf der Warenwirtschaft-Seite.
 * Scan mit dem Handy öffnet die login-freie Erfassung (/artikel-scan/{token}):
 * Artikelnummer eintippen → Artikel prüfen → Scan-Code mit der Kamera erfassen.
 * Jul-2026 (User-Wunsch): Klick = vergrößern + ausdruckbar (wie Handy-Wareneingang).
 */
import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Barcode, Printer, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { openQrPrint, dealerPrintInfo } from "../../lib/qrPrint";
import { QrLabelSheetButton } from "../QrLabelSheetButton";

export default function BarcodeCaptureQr() {
    const { user } = useAuth();
    const [session, setSession] = useState(null);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        api.post("/articles/barcode-scan-sessions?long=true").then((r) => setSession(r.data)).catch(() => setSession(null));
    }, []);
    if (!session?.portal_url) return null;

    const handlePrint = async () => {
        const ok = await openQrPrint({
            title: "Barcode nacherfassen per Handy",
            subtitle: "Mit der Handy-Kamera scannen — Artikelnummer eintippen, Artikel prüfen, dann den Barcode mit der Kamera erfassen und speichern (ohne Anmeldung).",
            scanUrl: session.portal_url,
            ...dealerPrintInfo(user),
        });
        if (!ok) toast.error("Popup blockiert — bitte Pop-ups erlauben");
    };

    return (
        <div className="relative shrink-0">
            <button type="button" onClick={() => setOpen((o) => !o)}
                className="flex flex-col items-center bg-white rounded-sm p-1.5 border border-gray-600 hover:ring-2 hover:ring-[#FFCC00] transition-shadow"
                data-testid="wms-barcode-capture-qr" title="Handy-Scan · Artikelnummer eingeben → Scan-Code nacherfassen (Klick: vergrößern & drucken)">
                <QRCodeSVG value={session.portal_url} size={64} level="M" />
                <div className="mt-1 text-[9px] uppercase tracking-wider font-black text-[#111827] inline-flex items-center gap-1">
                    <Barcode className="w-3 h-3" /> Barcode nacherfassen
                </div>
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-2 z-50 w-72 bg-white border border-gray-200 rounded-sm shadow-xl p-4 text-[#111827]" data-testid="wms-barcode-capture-qr-panel">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Barcode nacherfassen per Handy</span>
                        <button type="button" onClick={() => setOpen(false)} data-testid="wms-barcode-capture-qr-close"><X className="w-4 h-4 text-gray-500" /></button>
                    </div>
                    <div className="flex justify-center bg-white p-2 rounded-sm border-2 border-[#FFCC00]">
                        <QRCodeSVG value={session.portal_url} size={180} level="M" />
                    </div>
                    <p className="text-[11px] text-gray-600 mt-2">Mit dem Handy scannen: Artikelnummer eintippen → Artikel prüfen → Barcode mit der Kamera erfassen &amp; speichern (ohne Anmeldung).</p>
                    <button type="button" onClick={handlePrint}
                        className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-black"
                        data-testid="wms-barcode-capture-qr-print">
                        <Printer className="w-4 h-4" /> QR-Code drucken
                    </button>
                    <QrLabelSheetButton scanUrl={session.portal_url}
                        label="Barcode nacherfassen per Handy — QR scannen"
                        testidPrefix="wms-barcode-capture-qr-label" />
                </div>
            )}
        </div>
    );
}
